import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getEvent, deleteEvent, listEventRsvps, setRsvpStatus, removeRsvp,
} from "@/lib/events";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Calendar, MapPin, Users, Edit3, Trash2, Check, X,
  MessageCircle, Instagram, Clock, ShieldCheck, EyeOff, Share2,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "@/components/ConfirmModal";
import ShareEventModal from "@/components/SharePromptModal";
import Avatar from "@/components/Avatar";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ManageEventPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const refresh = async () => {
    const [e, r] = await Promise.all([getEvent(id, user?.id), listEventRsvps(id)]);
    setEvent(e);
    setRsvps(r);
  };

  useEffect(() => {
    if (!id || !user?.id) return;
    setLoading(true);
    refresh().catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  if (loading) {
    return <CenterMsg>Loading…</CenterMsg>;
  }
  if (!event) {
    return <CenterMsg>Event not found.</CenterMsg>;
  }
  if (event.host_id !== user.id) {
    return (
      <CenterMsg>
        Only the host can manage this event.
        <Link to={`/events/${id}`} className="block mt-4 text-ink hover:text-ink text-xs uppercase tracking-widest">
          ← View event
        </Link>
      </CenterMsg>
    );
  }

  const pending = rsvps.filter((r) => r.status === "pending");
  const approved = rsvps.filter((r) => r.status === "approved");
  const rejected = rsvps.filter((r) => r.status === "rejected");

  const onApprove = async (rsvpId) => {
    setBusyId(rsvpId);
    try {
      await setRsvpStatus(rsvpId, "approved");
      await refresh();
      toast.success("Approved.");
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusyId(null); }
  };
  const onReject = async (rsvpId) => {
    setBusyId(rsvpId);
    try {
      await setRsvpStatus(rsvpId, "rejected");
      await refresh();
      toast.success("Rejected.");
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusyId(null); }
  };
  const onRemove = async (rsvpId) => {
    setBusyId(rsvpId);
    try {
      await removeRsvp(rsvpId);
      await refresh();
      toast.success("Removed.");
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusyId(null); }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteEvent(id);
      toast.success("Event deleted.");
      navigate("/my-events");
    } catch (e) {
      toast.error(fmtError(e));
      setDeleting(false);
    }
  };

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-20">
        <Link
          to="/my-events"
          data-testid="manage-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors mb-8"
        >
          <ArrowLeft size={14} strokeWidth={2} /> Back to my events
        </Link>

        {/* Header */}
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.3em] text-ink">Manage</span>
          <h1 data-testid="manage-event-title" className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-2 leading-[0.95]">
            {event.title}
          </h1>
          <div className="mt-4 flex flex-wrap gap-3 text-xs uppercase tracking-widest text-ink/70">
            <Tag><Calendar size={12} strokeWidth={1.8} className="text-ink" />{formatDate(event.date_time)}</Tag>
            <Tag><MapPin size={12} strokeWidth={1.8} className="text-ink" />{event.city}</Tag>
            <Tag><Users size={12} strokeWidth={1.8} className="text-ink" />{approved.length}{event.capacity ? `/${event.capacity}` : ""} going</Tag>
            {event.requires_approval && <Tag><ShieldCheck size={12} strokeWidth={1.8} className="text-ink" />approval required</Tag>}
            {event.is_private && <Tag><EyeOff size={12} strokeWidth={1.8} className="text-ink" />private</Tag>}
          </div>
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap gap-3 mb-12">
          <Link
            to={`/events/${id}`}
            data-testid="manage-view-public"
            className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-paper hover:text-ink px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            View public page
          </Link>
          <Link
            to={`/events/${id}/edit`}
            data-testid="manage-edit"
            className="inline-flex items-center gap-2 bg-neon text-ink hover:bg-paper px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            <Edit3 size={14} strokeWidth={2.5} /> Edit
          </Link>
          <button
            data-testid="manage-share"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-paper hover:text-ink px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            <Share2 size={14} strokeWidth={2.5} /> Share
          </button>
          <button
            data-testid="manage-delete"
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center gap-2 border border-destructive text-destructive hover:bg-destructive hover:text-ink px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            <Trash2 size={14} strokeWidth={2} /> Delete
          </button>
        </div>

        {/* Pending requests */}
        {event.requires_approval && (
          <Section
            testid="manage-pending"
            title="Pending requests"
            count={pending.length}
            empty="No requests waiting on you."
            list={pending}
            renderItem={(r) => (
              <RsvpRow
                key={r.id} r={r} busy={busyId === r.id}
                actions={
                  <>
                    <RowBtn data-testid={`approve-${r.id}`} onClick={() => onApprove(r.id)} disabled={busyId === r.id}>
                      <Check size={12} strokeWidth={3} /> Approve
                    </RowBtn>
                    <RowBtn variant="ghost" data-testid={`reject-${r.id}`} onClick={() => onReject(r.id)} disabled={busyId === r.id}>
                      <X size={12} strokeWidth={3} /> Reject
                    </RowBtn>
                  </>
                }
              />
            )}
          />
        )}

        {/* Going */}
        <Section
          testid="manage-going"
          title="Going"
          count={approved.length}
          empty="Nobody's said yes yet."
          list={approved}
          renderItem={(r) => (
            <RsvpRow
              key={r.id} r={r} busy={busyId === r.id}
              actions={
                <RowBtn variant="ghost" data-testid={`remove-${r.id}`} onClick={() => onRemove(r.id)} disabled={busyId === r.id}>
                  <X size={12} strokeWidth={3} /> Remove
                </RowBtn>
              }
            />
          )}
        />

        {/* Rejected */}
        {rejected.length > 0 && (
          <Section
            testid="manage-rejected"
            title="Rejected"
            count={rejected.length}
            empty=""
            list={rejected}
            renderItem={(r) => (
              <RsvpRow
                key={r.id} r={r} busy={busyId === r.id}
                actions={
                  <RowBtn data-testid={`reapprove-${r.id}`} onClick={() => onApprove(r.id)} disabled={busyId === r.id}>
                    <Check size={12} strokeWidth={3} /> Approve
                  </RowBtn>
                }
              />
            )}
          />
        )}

        {event.additional_notes && (
          <div className="mt-12 border border-ink/15 p-6">
            <span className="text-xs uppercase tracking-widest text-ink/50">Host notes (visible to attendees)</span>
            <p className="mt-2 text-ink whitespace-pre-line leading-relaxed">{event.additional_notes}</p>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirmDelete}
        title="Delete this event?"
        body="This will remove the event and all RSVPs. There's no undo."
        confirmLabel="Delete event"
        destructive
        loading={deleting}
        onCancel={() => !deleting && setConfirmDelete(false)}
        onConfirm={onDelete}
      />
      {shareOpen && (
        <ShareEventModal event={event} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}

function CenterMsg({ children }) {
  return (
    <div className="bg-paper min-h-[60vh] flex items-center justify-center px-6 text-center text-ink/70 text-sm uppercase tracking-widest">
      <div>{children}</div>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-1.5">{children}</span>
  );
}

function Section({ testid, title, count, empty, list, renderItem }) {
  return (
    <section data-testid={testid} className="mb-12">
      <div className="flex items-end justify-between mb-4">
        <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
          {title} <span className="text-ink/40">{count}</span>
        </h2>
      </div>
      {list.length === 0 ? (
        empty ? <p className="text-ink/50 text-sm border border-dashed border-ink/15 p-6">{empty}</p> : null
      ) : (
        <div className="border border-ink/15 divide-y divide-border">
          {list.map(renderItem)}
        </div>
      )}
    </section>
  );
}

function RsvpRow({ r, actions, busy }) {
  return (
    <div data-testid={`rsvp-row-${r.id}`} className={`flex items-center gap-4 px-4 md:px-5 py-4 transition-opacity ${busy ? "opacity-60" : ""}`}>
      <Avatar
        src={r.avatar_url}
        name={r.display_name}
        size="md"
        to={r.username ? `/u/${r.username}` : (r.user_id ? `/u/${r.user_id}` : null)}
      />
      <div className="flex-1 min-w-0">
        <p className="text-ink font-medium truncate">
          {r.username ? (
            <Link to={`/u/${r.username}`} className="hover:text-ink transition-colors">
              {r.display_name} <span className="text-ink/40 font-normal text-xs">@{r.username}</span>
            </Link>
          ) : r.display_name}
        </p>
        <div className="flex flex-wrap gap-3 text-xs text-ink/60 mt-1">
          {r.whatsapp ? (
            <a
              href={`https://wa.me/${r.whatsapp.replace(/[^0-9+]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-ink transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MessageCircle size={12} strokeWidth={1.8} /> {r.whatsapp}
            </a>
          ) : null}
          {r.instagram ? (
            <a
              href={`https://instagram.com/${r.instagram.replace(/^@/, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-ink transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Instagram size={12} strokeWidth={1.8} /> @{r.instagram.replace(/^@/, "")}
            </a>
          ) : null}
          <span className="inline-flex items-center gap-1.5 text-ink/40">
            <Clock size={12} strokeWidth={1.8} /> {formatDate(r.created_at)}
          </span>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">{actions}</div>
    </div>
  );
}

function RowBtn({ children, variant = "primary", ...props }) {
  const base =
    "inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-60";
  const styles =
    variant === "primary"
      ? "bg-neon text-ink hover:bg-paper"
      : "border border-ink/15 text-ink hover:border-ink";
  return (
    <button {...props} className={`${base} ${styles}`}>
      {children}
    </button>
  );
}
